// INVARIANT: All public client methods return ApiResult<T>, never raw payloads.
import * as v from 'valibot';
export class ContractViolationError extends Error {
    endpoint;
    direction;
    issues;
    data;
    requestId;
    status;
    constructor(endpoint, direction, issues, // Fixed type
    data, requestId, status) {
        super(`Contract violation in ${direction} for ${endpoint}`);
        this.endpoint = endpoint;
        this.direction = direction;
        this.issues = issues;
        this.data = data;
        this.requestId = requestId;
        this.status = status;
        this.name = 'ContractViolationError';
    }
    getSummary() {
        const issues = this.issues
            .map((i) => `${i.path?.map((p) => p.key).join('.')}: ${i.message}`)
            .join(', ');
        return `[${this.endpoint}] ${this.direction} violation: ${issues}`;
    }
}
export class ApiClientError extends Error {
    code;
    details;
    requestId;
    constructor(code, message, details, requestId) {
        super(message);
        this.name = 'ApiClientError';
        this.code = code;
        this.details = details;
        this.requestId = requestId;
    }
}
import { CONTRACT_HASH } from '@sisumail/contracts';
/**
 * Derive validity from raw envelope.
 * Single source of truth - called only at API boundary.
 */
export function deriveValidity(envelope) {
    if (envelope._contract_warning)
        return 'warned';
    if (envelope._contract_checked)
        return 'checked';
    return 'unknown';
}
// Handshake Response Validator
const MetaContractResponseSchema = v.object({
    hash: v.string(),
    build: v.string(),
    ts: v.number(),
});
// =============================================================================
// Helper: Request Normalization
// =============================================================================
/**
 * Normalizes request data to match contract semantics.
 * - "" (Empty String) -> undefined
 * - null -> undefined
 * - Recursive for objects/arrays
 * - Preserves 0, false, [], {}
 */
function normalizeRequest(input) {
    if (input === null)
        return undefined;
    if (input === '')
        return undefined;
    if (Array.isArray(input)) {
        return input.map(normalizeRequest);
    }
    if (typeof input === 'object') {
        // v.file() / Blob / Buffer support - don't touch special objects?
        // For JSON API, usually just plain objects.
        // Let's assume plain objects for now or verify prototype.
        if (input.constructor !== Object && input.constructor !== Array) {
            return input; // Pass through special objects (FormData, etc - though we mostly use JSON)
        }
        const out = {};
        for (const [key, val] of Object.entries(input)) {
            const normalized = normalizeRequest(val);
            if (normalized !== undefined) {
                out[key] = normalized;
            }
        }
        return out;
    }
    return input;
}
export class ContractClient {
    config;
    constructor(config) {
        this.config = config;
    }
    /**
     * Check Version Handshake
     * Throws ContractViolationError if server contract hash does not match client bundled hash.
     */
    async checkVersion() {
        const url = `${this.config.baseUrl}/meta/contract`;
        let init = { method: 'GET' };
        if (this.config.onRequest)
            init = this.config.onRequest(init);
        const response = await fetch(url, init);
        if (!response.ok) {
            // If meta endpoint is missing (404), likely old API version
            throw new Error(`Failed to fetch contract version: ${response.status}`);
        }
        const json = (await response.json());
        if (!json.ok) {
            throw new ApiClientError(json.error.code, json.error.message, json.error.details);
        }
        const result = v.safeParse(MetaContractResponseSchema, json.data);
        if (!result.success) {
            throw new Error('Invalid contract metadata response format');
        }
        const serverHash = result.output.hash;
        if (serverHash !== CONTRACT_HASH) {
            const error = new ContractViolationError('meta.contract', 'response', [], // No schema issues, hash mismatch
            { client: CONTRACT_HASH, server: serverHash }, undefined, response.status);
            // Re-purpose as Version Mismatch
            error.name = 'ContractVersionMismatchError';
            error.message = `Contract Version Mismatch: Client=${CONTRACT_HASH.slice(0, 8)}, Server=${serverHash.slice(0, 8)}`;
            if (this.config.onContractViolation)
                this.config.onContractViolation(error);
            throw error;
        }
    }
    /**
     * Typed API Call
     */
    async call(endpoint, params) {
        const isDev = this.config.env === 'development' || this.config.env === 'preview';
        // 1. Normalize Request
        // We cast params to unknown then normalizer handles it.
        // We trust the schema to validate the normalized output.
        const normalizedParams = normalizeRequest(params);
        // 2. Validate Request
        const requestResult = v.safeParse(endpoint.request, normalizedParams); // Do not default to {}, allow undefined to pass validation
        if (!requestResult.success) {
            const error = new ContractViolationError(endpoint.alias, 'request', requestResult.issues, normalizedParams);
            if (this.config.onContractViolation) {
                this.config.onContractViolation(error);
            }
            throw error;
        }
        // 3. Prepare Request
        let url = `${this.config.baseUrl}${endpoint.path}`;
        // Path Parameter Substitution
        // We assume params contains keys matching :key in path.
        // We iterate keys in params; if they match a path segment, we replace and remove from query/body.
        const pathParams = new Set();
        // Simple regex to find :param
        const pathKeys = (endpoint.path.match(/:[a-zA-Z0-9_]+/g) || []).map((k) => k.substring(1));
        // Use normalized params for execution
        const safeParams = (normalizedParams || {});
        if (pathKeys.length > 0) {
            for (const key of pathKeys) {
                if (key in safeParams) {
                    const val = safeParams[key];
                    url = url.replace(`:${key}`, encodeURIComponent(String(val)));
                    pathParams.add(key);
                }
                else {
                    // Start of rudimentary error; missing path param
                    throw new Error(`Missing path parameter: ${key} for ${endpoint.alias}`);
                }
            }
        }
        // Filter out path params from remaining params
        const remainingParams = { ...safeParams };
        for (const key of pathParams) {
            delete remainingParams[key];
        }
        let init = {
            method: endpoint.method,
            headers: { 'Content-Type': 'application/json' },
        };
        if (this.config.credentials)
            init.credentials = this.config.credentials;
        // DIAMOND: Attach Contract & Trace headers for Level 4 Safety
        const headers = init.headers;
        headers['X-Contract-Hash'] = CONTRACT_HASH;
        headers['X-Trace-Id'] = globalThis.crypto.randomUUID();
        if (this.config.deviceKey) {
            headers['Authorization'] =
                `DeviceKey ${this.config.deviceKey.id}:${this.config.deviceKey.secret}`;
        }
        // Handle Body vs Query
        if (endpoint.method === 'GET') {
            if (Object.keys(remainingParams).length > 0) {
                const qs = new URLSearchParams(remainingParams).toString();
                url += (url.includes('?') ? '&' : '?') + qs;
            }
        }
        else {
            // Only send body if we have params or input was explicit object
            // If normalizedParams is undefined, and we have no path params consuming it, we send nothing?
            // Safer: if keys exist in remainingParams OR if normalizedParams was not undefined (explicit empty object)
            if (normalizedParams !== undefined) {
                init.body = JSON.stringify(remainingParams);
            }
        }
        if (this.config.onRequest)
            init = this.config.onRequest(init);
        // 3. Execute
        const response = await fetch(url, init);
        let json;
        try {
            json = (await response.json());
        }
        catch (e) {
            // Non-JSON response?
            throw new Error(`Invalid JSON response from ${endpoint.alias}: ${response.status}`);
        }
        const requestId = json.error?.requestId || response.headers.get('X-Request-Id') || undefined;
        // 4. Validate Envelope
        if (!json.ok) {
            const error = json.error;
            if (this.config.onError)
                this.config.onError(error);
            throw new ApiClientError(error.code, error.message, error.details, requestId);
        }
        // 5. Validate Response Data
        // Always validate response in dev/preview to catch server drift
        const result = v.safeParse(endpoint.response, json.data);
        if (!result.success) {
            const error = new ContractViolationError(endpoint.alias, 'response', result.issues, json.data, requestId, response.status);
            if (this.config.onContractViolation)
                this.config.onContractViolation(error);
            throw error;
        }
        // Return validated data in ApiResult envelope
        return {
            data: result.output,
            validity: deriveValidity(json),
            _contract_warning: json._contract_warning,
        };
    }
}
//# sourceMappingURL=client.js.map